import clsx from 'clsx';
import MutedText from 'components/common/MutedText';
import { HubertBadge } from 'components/HubertUI/HubertBadge';
import { Trash2 } from 'lucide-react';
import type { ComponentProps } from 'react';
import { useMemo, useState } from 'react';
import { EFormType } from '@hubertai/screening-question-types';
import { ECreateQuestionMode, useQuestionEditorContext } from '../../../context/create-question.context';
import CreateFormCheckbox from '../CreateFormCheckbox';
import CreateFormEmail from '../CreateFormEmail';
import CreateFormFile from '../CreateFormFile';
import CreateFormMatrix from '../CreateFormMatrix';
import CreateFormNumber from '../CreateFormNumber';
import CreateFormPhone from '../CreateFormPhone';
import CreateFormRange from '../CreateFormRange';
import CreateFormSlider from '../CreateFormSlider';
import CreateFormText from '../CreateFormText';
import type { TFormFieldUpdater } from '../FormFieldUpdater';
import FormFieldUpdater from '../FormFieldUpdater';
import { useAppDispatch, useAppSelector } from 'rtk/query/hooks';
import { selectQuestionMode } from 'rtk/questionEditorSelector';
import { removeForm } from 'rtk/Question.Editor.Slice';
import { type TFormSubType } from '@hubertai/screening-question-types';
import { useTranslation } from 'react-i18next';

interface Props extends ComponentProps<'div'> {
  data: {
    item: TFormSubType;
    index: number;
  };
}

const FormElement = ({ data, ...props }: Props) => {
  const { t } = useTranslation('Questionsv2');
  const [isFormOpen, setIsFormOpen] = useState<boolean>(false);
  const form = data.item;
  const formIndex = data.index;
  const dispatch = useAppDispatch();
  const { questionId } = useQuestionEditorContext();
  const mode = useAppSelector((state) => selectQuestionMode(state, questionId));
  const formFieldValues: TFormFieldUpdater[] = useMemo(
    () => [
      {
        label: t('form.element.isEnabled.label'),
        description: t('form.element.isEnabled.description'),
        formKey: 'enabled',
        fieldType: 'boolean',
        formValuesKey: 'isEnabled',
        form,
        formIndex,
      },
      {
        label: t('form.element.isMandatory.label'),
        description: t('form.element.isMandatory.description'),
        formKey: 'is_mandatory',
        fieldType: 'boolean',
        formValuesKey: 'isMandatory',
        form,
        formIndex,
      },
      {
        label: t('form.element.fieldLabel.label'),
        description: t('form.element.fieldLabel.description'),
        formKey: 'label',
        fieldType: 'rich-text',
        formValuesKey: 'label',
        form,
        formIndex,
      },
    ],
    [t, form, formIndex],
  );

  return (
    <div {...props} className={clsx('rounded-md', 'px-2 py-2', 'border-primary-200 border-1', 'w-full')}>
      <div
        className={clsx('flex flex-row justify-between', 'px-2 py-2', 'cursor-pointer')}
        onClick={() => {
          setIsFormOpen(!isFormOpen);
        }}
      >
        <HubertBadge variant={'secondary'}>{form.type}</HubertBadge>

        <div className="flex flex-row items-center justify-end gap-2">
          <MutedText className="text-sm">{form.id}</MutedText>
          <div className={clsx(mode === ECreateQuestionMode.InTemplateEdit && 'hidden')}>
            <Trash2
              className="text-danger-500 cursor-pointer"
              onClick={() => {
                dispatch(removeForm({ questionId, formIndex }));
              }}
            />
          </div>
        </div>
      </div>

      <div
        className={clsx(
          'flex flex-col justify-between gap-8',
          `${isFormOpen ? '' : 'hidden h-0'} ease transition-all duration-300`,
          'mt-8',
        )}
      >
        {formFieldValues.map((x, index) => {
          return (
            <FormFieldUpdater
              key={`form-field-updater-${form.id}-${index}`}
              label={x.label}
              description={x.description}
              formKey={x.formKey}
              fieldType={x.fieldType}
              formValuesKey={x.formValuesKey}
              form={x.form}
              formIndex={x.formIndex}
            />
          );
        })}

        {(() => {
          if (form.type === EFormType.Text) {
            return <CreateFormText form={form} formIndex={formIndex} />;
          } else if (form.type === EFormType.Slider) {
            return <CreateFormSlider form={form} formIndex={formIndex} />;
          } else if (form.type === EFormType.Checkbox) {
            return <CreateFormCheckbox questionId={questionId} form={form} formIndex={formIndex} />;
          } else if (form.type === EFormType.Email) {
            return <CreateFormEmail form={form} formIndex={formIndex} />;
          } else if (form.type === EFormType.Matrix) {
            return <CreateFormMatrix form={form} formIndex={formIndex} />;
          } else if (form.type === EFormType.Range) {
            return <CreateFormRange form={form} formIndex={formIndex} />;
          } else if (form.type === EFormType.Number) {
            return <CreateFormNumber form={form} formIndex={formIndex} />;
          } else if (form.type === EFormType.File) {
            return <CreateFormFile />;
          } else if (form.type === EFormType.Phone) {
            return <CreateFormPhone form={form} formIndex={formIndex} />;
          } else {
            return <div>{t('form.element.wrongType')}.</div>;
          }
        })()}
      </div>
    </div>
  );
};

export default FormElement;
